<template>
  <subform-controller
    :form-observer="formObserver"
    :show-confirm-modal="showConfirmModal"
    :is-subform-visible="questionData.hadAnyAccidents"
    :form-items="questionData.claims"
    :subform="FormClaimAndConviction"
    :questions="questions"
    :product-name="productName"
    :max-amount="constants.CLAIMS_MAX_QTY"
    :payload-template="claimFields()"
    :get-rules="helpers.getClaimsRules"
    item-name="claim"
    :step-id="stepId"
    section-item-name="claims"
    @set-items="questionData.claims = $event"
    @set-question="questionData.hadAnyAccidents = $event"
  >
    <template #question="{ observer }">
      <form-row
        id="claims"
        :label="isAdditional ? titleAdditional : title"
        :help-text="isAdditional ? helpText.valueAdditional : helpText.value"
        :error="formObserver.validated && !observer.valid"
      >
        <validation-provider
          v-slot="{ failed }"
          name="Had Any Accidents"
          rules="required"
          :vid="`${stepId}_had-any-accidents`"
        >
          <radio
            v-model="questionData.hadAnyAccidents"
            :name="`${stepId}.had-any-accidents`"
            :error="failed"
            :items="constants.YES_NO"
            :data-test="snakeCase(questionFields.hadAnyAccidents)"
          />
        </validation-provider>
      </form-row>
    </template>
    <template
      #preview="{ item: { item } }"
    >
      <subform-preview
        :preview-list="getFormattedSubformPreviewList(item)"
        :icon="vehicleIcon"
      />
    </template>
  </subform-controller>
</template>

<script>
import QuestionBase from '@/components/questions/Base';
import SubformController from '@/components/SubformController/SubformController';
import SubformPreview from '@/components/SubformController/SubformPreview';
import FormClaimAndConviction from '@/components/Subforms/FormClaimAndConviction';
import useSubForm from '@/composables/useSubForm';

import { getNumberFormatted } from '@/helpers/common';
import { mapActions } from 'vuex';

export default {
  name: 'question-base-claims',
  extends: QuestionBase,
  components: {
    SubformController,
    SubformPreview,
  },
  props: {
    questions: {
      type: Array,
      required: true,
    },
  },
  data () {
    return {
      fields: ['hadAnyAccidents', 'claims'],
    };
  },
  methods: {
    ...mapActions(['showConfirmModal']),
    claimFields () {
      return this.questions.reduce((acc, question) => {
        const field = question.params?.customFields?.field;
        if (field) {
          acc[field] = null;
        }
        return acc;
      }, {});
    },
    getFormattedSubformPreviewList (item) {
      const isNoClaimMadeDescription = item.causeOfDescription === this.constants.DRIVER_CLAIM_CAUSE_INCIDENT_NO_CLAIM_MADE;

      const mappings = {
        causeOfDescription: {
          prepend: 'Cause:',
          value: this.constants.DRIVER_CLAIM_CAUSE_OF_DESCRIPTION[item.causeOfDescription],
        },
        ...(item.costOfRepairs !== null && !isNoClaimMadeDescription
          ? {
            costOfRepairs: {
              prepend: 'Total claim cost',
              value: `£ ${getNumberFormatted(item.costOfRepairs)}`,
            },
          }
          : {}),
        dateOfClaim: {
          prepend: 'Happened on',
          value: item.dateOfClaim,
        },
        ...(item.causeOfDescription === this.constants.CAUSE_OF_DESCRIPTION_ACCIDENT
          ? {
            settledClaim: {
              prepend: '',
              value: item.settledClaim ? 'Settled' : 'Not settled',
              append: 'claim',
            },
            anyInjuries: {
              prepend: '',
              value: `With${!item.anyInjuries ? 'out' : ''}`,
              append: 'injuries',
            },
          }
          : {}),
        ...(item.settledClaim === this.constants.CLAIM_SETTLED
          ? {
            singleAccident: {
              prepend: '',
              value: `${!item.singleAccident ? 'Not a ' : ''}Single`,
              append: 'Accident',
            },
          }
          : {}),
        atFault: {
          prepend: '',
          value: this.constants?.DRIVER_CLAIM_AT_FAULT?.[item.atFault] ?? null,
          append: 'at fault',
        },
        typeTheft: {
          prepend: 'Stolen:',
          value: this.constants?.DRIVER_CLAIM_TYPE_THEFT?.[item.typeTheft] ?? null,
        },
        ...(item.typeTheft === this.constants.TYPE_THEFT_VEHICLE_CONTENTS
          ? {
            recoveredVehicle: {
              prepend: '',
              value: item.recoveredVehicle ? 'Recovered' : 'Not recovered',
              append: 'vehicle',
            },
          }
          : {}),
        ...([this.constants.TYPE_THEFT_VEHICLE_CONTENTS, this.constants.TYPE_THEFT_CONTENTS_OF_THE_VEHICLE].includes(item.typeTheft)
          ? {
            theftFromHome: {
              prepend: 'Theft',
              value: `${!item.theftFromHome ? 'not ' : ''}from home`,
            },
          }
          : {}),
        currentPolicy: {
          prepend: 'Occurred on recent policy:',
          value: `${item.currentPolicy ? 'Yes' : 'No'}`,
        },
      };

      return this.generateClaimsConvictionsPreviewList(this.questionData.claims, item.id, mappings);
    },
  },
  setup (props, context) {
    const { vehicleIcon, generateClaimsConvictionsPreviewList } = useSubForm(null, context);
    return {
      FormClaimAndConviction,

      // composable
      vehicleIcon,
      generateClaimsConvictionsPreviewList,
    };
  },
};
</script>

<style lang="scss" scoped>
.summary-item-wrap {
  list-style: disc;
  margin-left: 5px;
}
</style>
